import { BarChart3 } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ApiError, endpoints, type PowerBiEmbed } from '../../services/api';
import { useScreen, useSharedSource } from '../../store/screen';
import { startMicrosoft, startPowerBi, useSession } from '../../store/session';
import { useSettings, type PowerBiItem } from '../../store/settings';

// Power BI's own embedding library: loaded only when a report is on screen, so screens
// without Power BI never download it.
type PowerBiClient = typeof import('powerbi-client');
let client: Promise<PowerBiClient> | null = null;
const loadClient = () => {
  quietPowerBiRejections();
  return (client ??= import('powerbi-client'));
};

/** Power BI's error objects: { activityId, requestId, status } or { message, detailedMessage, errorCode, level }. */
// (Sometimes an Error carrying these fields – the field names are what identify them.)
const isPowerBiError = (r: unknown): r is { status?: number; message?: string; detailedMessage?: string } =>
  typeof r === 'object' && r !== null && ('activityId' in r || 'detailedMessage' in r || 'errorCode' in r);

// Power BI's library rejects some internal promises (e.g. when Power BI refuses a token) without
// handling them. Those are reported to the widget as 'error' events or by `failed` below; here
// they're kept from surfacing as uncaught errors. Other rejections are left alone.
const failures = new Set<(r: { status?: number; message?: string; detailedMessage?: string }) => void>();
let quietened = false;
function quietPowerBiRejections(): void {
  if (quietened) return;
  quietened = true;
  window.addEventListener('unhandledrejection', (e) => {
    if (!isPowerBiError(e.reason)) return;
    e.preventDefault();
    failures.forEach((f) => f(e.reason as { status?: number }));
  });
}

/** Fetch a new token this long before the current one expires. */
const RENEW_BEFORE_MS = 5 * 60_000;

/**
 * Power BI reports and dashboards (Settings → Widgets → Power BI), embedded exactly as Power
 * BI shows them. With several chosen, tabs switch between them and they can rotate. Tokens
 * are renewed before they expire and report visuals reloaded on a timer, so a screen can
 * show the same report for days.
 */
export function PowerBI({ fill = false }: { fill?: boolean }) {
  const microsoft = useSession((s) => s.microsoft);
  const { items, rotateSec } = useSettings((s) => s.powerbi);
  const shared = useSharedSource('powerbi');
  const owner = useScreen((s) => s.canvas?.ownerEmail ?? null);
  const [index, setIndex] = useState(0);
  const active: PowerBiItem | undefined = items[index % Math.max(1, items.length)];
  const hasPowerBi = Boolean(microsoft?.accounts?.some((a) => a.connected && a.powerbi));

  useEffect(() => {
    if (items.length < 2 || rotateSec <= 0) return;
    const t = window.setInterval(() => setIndex((i) => (i + 1) % items.length), rotateSec * 1000);
    return () => window.clearInterval(t);
  }, [items.length, rotateSec, index]);

  let body;
  if (!shared && !microsoft?.connected) {
    body = (
      <Notice>
        Connect your work or school Microsoft account in Settings → Account, then Power BI in Settings → Widgets → Power BI.
        <button className="mt-2 block text-[var(--accent)] hover:underline" onClick={() => startMicrosoft('link')}>
          Connect Microsoft
        </button>
      </Notice>
    );
  } else if (!shared && !hasPowerBi) {
    body = (
      <Notice>
        Allow KCL Canvas to show your Power BI reports.
        <button className="mt-2 block text-[var(--accent)] hover:underline" onClick={() => startPowerBi()}>
          Connect Power BI
        </button>
      </Notice>
    );
  } else if (!active) {
    body = <Notice>Choose a report or dashboard in Settings → Widgets → Power BI.</Notice>;
  } else {
    body = <Embedded key={`${shared ?? ''}:${active.kind}:${active.id}:${active.pageName ?? ''}`} item={active} canvas={shared} owner={owner} />;
  }

  return (
    <div className={`glass flex flex-col overflow-hidden p-3 ${fill ? 'h-full' : 'h-[32rem]'}`}>
      <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        <div className="font-heading flex min-w-0 items-center gap-2 text-sm font-medium uppercase tracking-wider opacity-80">
          <BarChart3 className="h-4 w-4 shrink-0" aria-hidden />
          <span className="truncate">{active ? `${active.name}${active.pageTitle ? ` · ${active.pageTitle}` : ''}` : 'Power BI'}</span>
        </div>
        {items.length > 1 && (
          <div className="ml-auto flex flex-wrap gap-1" role="tablist" aria-label="Reports">
            {items.map((it, i) => (
              <button
                key={`${it.kind}:${it.id}:${it.pageName ?? ''}`}
                role="tab"
                aria-selected={it === active}
                onClick={() => setIndex(i)}
                className={`rounded-full px-2.5 py-0.5 text-xs transition ${it === active ? 'bg-[var(--accent)] text-black' : 'opacity-70 hover:opacity-100'}`}
              >
                {it.pageTitle ? `${it.name} · ${it.pageTitle}` : it.name}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="relative min-h-0 flex-1">{body}</div>
    </div>
  );
}

function Notice({ children }: { children: ReactNode }) {
  return <div className="muted p-2 text-sm">{children}</div>;
}

function Embedded({ item, canvas, owner }: { item: PowerBiItem; canvas: string | null; owner: string | null }) {
  const { fit, transparent, refreshMin } = useSettings((s) => s.powerbi);
  const host = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    let cancelled = false;
    let renew: number | undefined;
    let refresh: number | undefined;
    let embed: import('powerbi-client').Embed | null = null;
    // Power BI refused the token or the report (expired, no access): say so in the widget.
    const failed = (r: { status?: number; message?: string; detailedMessage?: string }) => {
      if (cancelled || !embed) return;
      setLoading(false);
      setError(
        r.status === 403 || r.status === 401
          ? `Power BI refused access to this${canvas ? `. Ask ${owner ?? 'whoever shared it'} to share it with you in Power BI` : ' – check you can open it in Power BI, or connect Power BI again in Settings → Widgets → Power BI'}.`
          : r.detailedMessage || r.message || 'Power BI couldn’t show this.',
      );
    };
    failures.add(failed);

    const fetchEmbed = () => endpoints.powerbiEmbed(item.kind, item.id, item.workspaceId, item.account, canvas).then((r) => r.embed);
    const scheduleRenew = (e: PowerBiEmbed) => {
      const wait = Math.max(30_000, new Date(e.expiresAt).getTime() - Date.now() - RENEW_BEFORE_MS);
      renew = window.setTimeout(async () => {
        try {
          const next = await fetchEmbed();
          if (cancelled) return;
          await embed?.setAccessToken(next.accessToken);
          scheduleRenew(next);
        } catch {
          renew = window.setTimeout(() => scheduleRenew(e), 60_000);
        }
      }, wait);
    };

    (async () => {
      try {
        const [pbiClient, info] = await Promise.all([loadClient(), fetchEmbed()]);
        if (cancelled) return;
        const { models } = pbiClient;
        const common = {
          id: info.id,
          embedUrl: info.embedUrl,
          accessToken: info.accessToken,
          tokenType: info.tokenType === 'aad' ? models.TokenType.Aad : models.TokenType.Embed,
        };
        const config =
          item.kind === 'report'
            ? {
                ...common,
                type: 'report',
                ...(item.pageName ? { pageName: item.pageName } : {}),
                settings: {
                  panes: { filters: { visible: false }, pageNavigation: { visible: false } },
                  background: transparent ? models.BackgroundType.Transparent : models.BackgroundType.Default,
                  layoutType: models.LayoutType.Custom,
                  customLayout: { displayOption: fit === 'width' ? models.DisplayOption.FitToWidth : models.DisplayOption.FitToPage },
                },
              }
            : { ...common, type: 'dashboard', pageView: 'fitToWidth' as const };
        embed = window.powerbi.embed(el, config);
        embed.on('loaded', () => !cancelled && setLoading(false));
        embed.on('error', (ev) => {
          const detail = (ev as { detail?: { message?: string; detailedMessage?: string } }).detail;
          if (!cancelled) setError(detail?.detailedMessage || detail?.message || 'Power BI couldn’t show this.');
        });
        scheduleRenew(info);
        if (item.kind === 'report' && refreshMin > 0) {
          refresh = window.setInterval(() => void (embed as import('powerbi-client').Report | null)?.refresh().catch(() => undefined), refreshMin * 60_000);
        }
      } catch (e) {
        if (cancelled) return;
        setLoading(false);
        if (e instanceof ApiError && e.code === 'no_access') {
          setError(
            `You don’t have access to this in Power BI. Ask ${owner ?? 'whoever shared it'} to share it with you in Power BI, then connect Power BI in Settings → Widgets → Power BI.`,
          );
        } else if (e instanceof ApiError && e.code === 'not_connected') {
          setError('Power BI needs connecting again: Settings → Widgets → Power BI.');
        } else {
          setError(e instanceof Error ? e.message : 'Couldn’t load Power BI.');
        }
      }
    })();

    return () => {
      cancelled = true;
      failures.delete(failed);
      window.clearTimeout(renew);
      window.clearInterval(refresh);
      try {
        window.powerbi?.reset(el);
      } catch {
        /* not embedded yet */
      }
    };
  }, [item.kind, item.id, item.workspaceId, item.pageName, item.account, canvas, owner, fit, transparent, refreshMin]);

  return (
    <>
      <div ref={host} className={`absolute inset-0 [&>iframe]:h-full [&>iframe]:w-full [&>iframe]:border-0 ${error ? 'hidden' : ''}`} />
      {loading && !error && <div className="muted absolute inset-0 grid place-items-center text-sm">Loading Power BI…</div>}
      {error && <div className="muted p-2 text-sm">{error}</div>}
    </>
  );
}
